import React from 'react';
import { BarChart3, Code, Rows } from 'lucide-react';

// Visual types that execute a query and therefore have an inspectable query.
const QUERYABLE_TYPES = ['table', 'pivot', 'kpi', 'bar_chart', 'line_chart', 'pie_chart', 'area_chart', 'loss_triangle'];
import { ReportElement } from '@/components/report-builder/ReportElement';
import { TableRenderer } from '@/components/report-builder/TableRenderer';
import { ChartRenderer } from '@/components/report-builder/ChartRenderer';
import { VisualErrorBoundary } from '@/components/report-builder/VisualErrorBoundary';

/**
 * Renderers for the report-mode multi-page canvas.
 *
 * `<ReportCanvas>` accepts three callback props (renderPageContent, renderHeader, renderFooter)
 * that receive a pageIdx and a sizes context, and return JSX. These factories build those
 * callbacks given the parent page's state.
 */

// ─── Page body — visuals assigned to a given page ───────────────────────────────
export function makeRenderPageContent(deps) {
  const {
    visuals, getTransformedData, visualResults,
    activeVisual, setActiveVisual, setActiveReportPageIndex,
    findAlignmentGuides, setAlignmentGuides,
    updateVisual, setSavedClean,
    handleVisualContextMenu, snap,
    getVizConfig, setVizConfig,
    formatValue, allFields, fieldOverrides,
    otherExpanded, toggleOtherExpanded,
    onViewQuery, globalSpan, asOfDate, modelId,
  } = deps;

  return (pageIdx) => {
    const pageVisualsList = visuals.filter(v => (v._reportPageIndex ?? 0) === pageIdx && !v._zone);
    return pageVisualsList.map(v => {
      const fp = v.freePosition || {
        x: 10,
        y: 10 + pageVisualsList.indexOf(v) * 50,
        w: 300,
        h: v.vizType === 'table' ? 300 : v.vizType === 'kpi' ? 60 : v.vizType === 'rich_text' ? 30 : 200,
      };
      const vResult = (visualResults[v.id] || {}).result;
      const vData = getTransformedData(v.id) || vResult?.data || [];

      return (
        <ReportElement
          key={v.id} id={v.id}
          x={fp.x} y={fp.y} w={fp.w} h={fp.h}
          zIndex={v.zIndex || 10}
          isActive={activeVisual === v.id}
          onSelect={() => { setActiveVisual(v.id); setActiveReportPageIndex(pageIdx); }}
          onPositionChange={(pos) => {
            const guides = findAlignmentGuides(pos.x, pos.y, pos.w, pos.h);
            setAlignmentGuides({ x: guides.x, y: guides.y });
            const finalPos = { ...pos, x: guides.snapX, y: guides.snapY };
            updateVisual(v.id, { freePosition: finalPos });
            setSavedClean(false);
          }}
          onDragEnd={() => setAlignmentGuides({ x: null, y: null })}
          onContextMenu={(e) => handleVisualContextMenu(e, v.id)}
          snapFn={snap}
          pageIndex={pageIdx}
        >
          {v.vizType === 'table' && (() => {
            const spans = v.spanPages !== undefined ? v.spanPages : (globalSpan !== false);
            return (
              <button
                onClick={(e) => { e.stopPropagation(); updateVisual(v.id, { spanPages: !spans }); setSavedClean(false); }}
                onMouseDown={(e) => e.stopPropagation()}
                className={`absolute top-1 right-9 z-20 inline-flex items-center gap-1 px-1.5 py-1 rounded-md border shadow-sm transition-colors text-[9px] font-medium ${spans ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-600' : 'border-gray-300 bg-white/90 text-gray-500'}`}
                title={spans ? 'This table spans across pages — click to keep it in a fixed box' : 'This table stays in a fixed box — click to let it span across pages'}
                data-testid={`span-toggle-${v.id}`}
              >
                <Rows className="w-3 h-3" /> {spans ? 'Spans pages' : 'Fixed'}
              </button>
            );
          })()}
          {onViewQuery && QUERYABLE_TYPES.includes(v.vizType) && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewQuery(v.id); }}
              onMouseDown={(e) => e.stopPropagation()}
              className="absolute top-1 right-1 z-20 inline-flex items-center justify-center p-1 rounded-md border border-blue-500/40 bg-white/90 text-blue-600 hover:bg-blue-500/10 shadow-sm transition-colors"
              title="View executed query"
              aria-label="View executed query"
              data-testid={`view-query-${v.id}`}
            >
              <Code className="w-3.5 h-3.5" />
            </button>
          )}
          <VisualErrorBoundary resetKey={`${fp.w}x${fp.h}`} label={v.title || v.vizType}>
          <ReportPageVisualBody
            v={v} vData={vData}
            updateVisual={updateVisual}
            getVizConfig={getVizConfig} setVizConfig={setVizConfig}
            formatValue={formatValue} allFields={allFields} fieldOverrides={fieldOverrides}
            otherExpanded={otherExpanded} toggleOtherExpanded={toggleOtherExpanded}
            asOfDate={asOfDate} modelId={modelId}
          />
          </VisualErrorBoundary>
        </ReportElement>
      );
    });
  };
}

function ReportPageVisualBody({ v, vData, updateVisual, getVizConfig, setVizConfig, formatValue, allFields, fieldOverrides, otherExpanded, toggleOtherExpanded, asOfDate, modelId }) {
  if (v.vizType === 'rich_text') {
    return (
      <div className="w-full h-full" onClick={e => e.stopPropagation()}>
        <textarea
          value={v.textContent || ''}
          onChange={e => updateVisual(v.id, { textContent: e.target.value })}
          className="w-full h-full bg-transparent border-none outline-none resize-none p-0 m-0"
          style={{
            fontSize: v.textSize || 14,
            fontWeight: v.textBold ? 'bold' : 'normal',
            fontStyle: v.textItalic ? 'italic' : 'normal',
            color: v.textColor || '#000',
            textAlign: v.textAlign || 'left',
            lineHeight: 1.2,
          }}
          placeholder="Type here..."
          onMouseDown={e => e.stopPropagation()}
        />
      </div>
    );
  }
  if (v.vizType === 'image') {
    return (
      <div className="w-full h-full flex items-center justify-center">
        {v.imageUrl
          ? <img src={v.imageUrl} alt="" className="max-w-full max-h-full object-contain" />
          : <div className="w-full h-full border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-[9px] text-gray-400">Set image URL</div>}
      </div>
    );
  }
  if (v.vizType === 'shape') {
    const isLine = v.shapeType === 'line_h' || !v.shapeType;
    return (
      <div className="w-full h-full">
        {isLine
          ? <div className="w-full" style={{ height: 2, background: v.shapeFill || '#000', marginTop: '50%' }} />
          : <div className="w-full h-full" style={{
              background: v.shapeFill || '#3b82f6',
              opacity: v.shapeOpacity || 0.3,
              border: `2px solid ${v.shapeBorder || '#1e40af'}`,
              borderRadius: v.shapeBorderRadius || 0,
            }} />}
      </div>
    );
  }
  if (v.vizType === 'kpi') {
    const measure = v.valueMeasures?.[0];
    const label = allFields.find(f => f.name === measure)?.description || measure || 'Add measure';
    const value = vData.length > 0 ? formatValue(vData[0]?.[measure]) : '--';
    const _fld = allFields.find(f => f.name === measure);
    const _tiOp = String(_fld?.time_intel?.op || '').toLowerCase();
    const _isToDate = ['ytd', 'qtd', 'mtd', 'itd'].includes(_tiOp)
      || /(^|[^a-z])(ytd|qtd|mtd|itd)([^a-z]|$)/i.test(measure || '')
      || /(year|quarter|month|inception)[- ]to[- ]date/i.test(measure || '');
    const _effAsOf = v.asOfDate || asOfDate;
    const _prior = vData.length > 0 ? vData[0]?.[measure + '__prior'] : undefined;
    const _priorPct = vData.length > 0 ? vData[0]?.[measure + '__prior_pct'] : undefined;
    const _hasPrior = _isToDate && _prior !== undefined && _prior !== null;
    return (
      <div className="flex items-center gap-2 h-full px-2">
        <div>
          <p className="text-[9px] text-gray-500 uppercase">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
          {_isToDate && (
            <p className="text-[8px] text-gray-400" data-testid={`kpi-as-of-${v.id}`}>as of {_effAsOf || 'today'}</p>
          )}
          {_hasPrior && (
            <p className={`text-[8px] ${typeof _priorPct === 'number' ? (_priorPct >= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-gray-400'}`} data-testid={`kpi-prior-${v.id}`}>
              {formatValue(_prior, measure)}{typeof _priorPct === 'number' ? ` · ${_priorPct >= 0 ? '+' : ''}${_priorPct}%` : ''} vs same point last year
            </p>
          )}
        </div>
      </div>
    );
  }
  if (v.vizType === 'table' && vData.length > 0) {
    return (
      <div className="w-full h-full min-h-0" data-visual-id={v.id}>
        <TableRenderer
          result={{ data: vData, row_count: vData.length }}
          vizConfig={getVizConfig(v.id)}
          formatValue={formatValue}
          allFields={allFields}
          fieldOverrides={fieldOverrides}
          rowDims={v.rowDims || []}
          onDrill={() => {}}
          onVizConfigChange={(cfg) => setVizConfig(v.id, cfg)}
        />
      </div>
    );
  }
  if (['bar_chart', 'line_chart', 'pie_chart', 'area_chart'].includes(v.vizType) && vData.length > 0) {
    const topNOn = !!getVizConfig(v.id)?.seriesTopN?.enabled;
    const expanded = !!(otherExpanded && otherExpanded[v.id]);
    return (
      <div className="w-full h-full relative" data-visual-id={v.id}>
        {topNOn && expanded && (
          <button onClick={() => toggleOtherExpanded && toggleOtherExpanded(v.id)} data-testid={`collapse-other-${v.id}`}
            className="absolute top-1 right-1 z-10 text-[9px] px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/40 text-blue-600 hover:bg-blue-500/20 transition-colors">
            ← Collapse “Other”
          </button>
        )}
        <ChartRenderer
          vizType={v.vizType} data={vData}
          rowDims={v.rowDims || []} colDim={v.colDim || ''}
          showGrid={true} showLegend={true}
          vizConfig={getVizConfig(v.id)}
          formatValue={formatValue} allFields={allFields} fieldOverrides={fieldOverrides}
          onDrill={() => {}} marks={{}} modelId={modelId} valueMeasures={v.valueMeasures}
          onOtherToggle={topNOn && toggleOtherExpanded ? () => toggleOtherExpanded(v.id) : undefined}
        />
      </div>
    );
  }
  return (
    <div className="w-full h-full border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center text-center p-2 bg-gray-50/50">
      <BarChart3 className="w-5 h-5 text-gray-300 mb-1" />
      <span className="text-[9px] text-gray-400 font-medium">{v.title || v.vizType}</span>
      <span className="text-[8px] text-gray-300">Double-click fields to add data</span>
    </div>
  );
}

// ─── Page header — repeats on every page ─────────────────────────────────────────
export function makeRenderPageHeader(deps) {
  const { visuals, reportHeader, reportName, activeVisual, setActiveVisual, updateVisual, setSavedClean, handleVisualContextMenu, snap } = deps;

  return () => {
    const headerVisuals = visuals.filter(v => v._zone === 'header');
    if (headerVisuals.length === 0 && reportHeader.title) {
      return (
        <div className="h-full flex items-center justify-between px-2">
          <span
            style={{ fontSize: reportHeader.titleFontSize || 16, fontWeight: reportHeader.titleBold ? 'bold' : 'normal' }}
            className="text-gray-800"
          >
            {(reportHeader.title || '').replace(/\{\{report_name\}\}/g, reportName || 'Report')}
          </span>
          {reportHeader.logoUrl && <img src={reportHeader.logoUrl} alt="Logo" className="h-8 object-contain" />}
        </div>
      );
    }
    return headerVisuals.map(v => {
      const fp = v.freePosition || { x: 10, y: 5, w: 200, h: 30 };
      return (
        <ReportElement
          key={v.id} id={v.id}
          x={fp.x} y={fp.y} w={fp.w} h={fp.h}
          zIndex={v.zIndex || 10}
          isActive={activeVisual === v.id}
          onSelect={() => setActiveVisual(v.id)}
          onPositionChange={(pos) => { updateVisual(v.id, { freePosition: pos }); setSavedClean(false); }}
          onContextMenu={(e) => handleVisualContextMenu(e, v.id)}
          snapFn={snap}
          zoneBadge="Pg Hdr"
        >
          {v.vizType === 'rich_text' ? (
            <div className="w-full h-full" onClick={e => e.stopPropagation()}>
              <textarea
                value={(v.textContent || '').replace(/\{\{report_name\}\}/g, reportName || 'Report')}
                onChange={e => updateVisual(v.id, { textContent: e.target.value })}
                className="w-full h-full bg-transparent border-none outline-none resize-none p-0 m-0"
                style={{
                  fontSize: v.textSize || 14,
                  fontWeight: v.textBold ? 'bold' : 'normal',
                  color: v.textColor || '#000',
                  textAlign: v.textAlign || 'left',
                }}
                placeholder="Header text..."
                onMouseDown={e => e.stopPropagation()}
              />
            </div>
          ) : v.vizType === 'image' ? (
            <div className="w-full h-full flex items-center justify-center">
              {v.imageUrl ? <img src={v.imageUrl} alt="" className="max-w-full max-h-full object-contain" /> : <div className="text-[8px] text-gray-400">Logo</div>}
            </div>
          ) : <div className="text-[8px] text-gray-400">{v.vizType}</div>}
        </ReportElement>
      );
    });
  };
}

// ─── Page footer — repeats on every page ─────────────────────────────────────────
export function makeRenderPageFooter(deps) {
  const { visuals, reportFooter, reportPages, activeVisual, setActiveVisual, updateVisual, setSavedClean, handleVisualContextMenu, snap } = deps;

  const fmtFooterText = (text, pageIdx) => (text || 'Page {{page}} of {{total_pages}}')
    .replace(/\{\{page\}\}/g, String(pageIdx + 1))
    .replace(/\{\{total_pages\}\}/g, String(reportPages.length))
    .replace(/\{\{date\}\}/g, new Date().toLocaleDateString());

  return (pageIdx) => {
    const footerVisuals = visuals.filter(v => v._zone === 'footer');
    if (footerVisuals.length === 0) {
      return (
        <div className="h-full flex items-center justify-center">
          <span className="text-[9px] text-gray-500">{fmtFooterText(reportFooter.text, pageIdx)}</span>
        </div>
      );
    }
    return footerVisuals.map(v => {
      const fp = v.freePosition || { x: 10, y: 5, w: 200, h: 20 };
      return (
        <ReportElement
          key={v.id} id={v.id}
          x={fp.x} y={fp.y} w={fp.w} h={fp.h}
          zIndex={v.zIndex || 10}
          isActive={activeVisual === v.id}
          onSelect={() => setActiveVisual(v.id)}
          onPositionChange={(pos) => { updateVisual(v.id, { freePosition: pos }); setSavedClean(false); }}
          onContextMenu={(e) => handleVisualContextMenu(e, v.id)}
          snapFn={snap}
          zoneBadge="Pg Ftr"
        >
          {v.vizType === 'rich_text' ? (
            <div className="w-full h-full" onClick={e => e.stopPropagation()}>
              <textarea
                value={fmtFooterText(v.textContent, pageIdx)}
                onChange={e => updateVisual(v.id, { textContent: e.target.value })}
                className="w-full h-full bg-transparent border-none outline-none resize-none p-0 m-0"
                style={{ fontSize: v.textSize || 9, color: v.textColor || '#666', textAlign: v.textAlign || 'center' }}
                placeholder="Footer text..."
                onMouseDown={e => e.stopPropagation()}
              />
            </div>
          ) : <div className="text-[8px] text-gray-400">{v.vizType}</div>}
        </ReportElement>
      );
    });
  };
}
